export type FormField<T> = {
    value: T;
    errorMessage: string[];
};

export type NullableFormField<T> = FormField<T | null>;

export type ReadOnlyFormField<T> = Pick<FormField<T>, 'value'>;

export interface SubmittedForm {
    submitted: boolean;
}

export function isUndefinedOrNull(value: any): boolean {
    return value === undefined || value === null;
}

export function isEmpty(value: any): boolean {
    return value === "";
}

export function isNullOrEmpty(value: any): boolean {
    return isUndefinedOrNull(value) || isEmpty(value);
}

export function isBetween(value: number, min: number, max: number): boolean {
    return min <= value && value <= max;
}

export function hasLengthBetween(value: { length: number }, minLength: number, maxLength: number): boolean {
    return isBetween(value.length, minLength, maxLength);
}

export function isInEnum<T>(value: any, enumType: T): boolean {
    return Object.values(enumType as any).includes(value);
}

export function isValidEmail(value: string): boolean {
    if (!hasLengthBetween(value, 3, 320)) {
        return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
}

export function isValidNif(value: string): boolean {
    return hasLengthBetween(value, 1, 20);

    //TODO: Implement NIF, CIF, NIE and DNI validation
    if (value.length !== 9) {
        return false;
    }

    const nif = value.slice(0, 8);
    const control = value.slice(8);

    const nifNumber = parseInt(nif, 10);
    if (isNaN(nifNumber)) {
        return false;
    }

    const remainder = nifNumber % 23;
    const controlLetter = "TRWAGMYFPDXBNJZSQVHLCKET".charAt(remainder);

    return controlLetter === control;
}

export function isValidPassword(value: string): boolean {
    let has_upper = false;
    let has_lower = false;
    let has_digit = false;
    let has_special = false;

    for (let i = 0; i < value.length; i++) {
        const c = value.charAt(i);

        if (c >= '0' && c <= '9') {
            has_digit = true;
        } else if (isLetter(c)) {
            // Check if it's an uppercase or lowercase letter (including unicode)
            if (c.toUpperCase() === c) {
                has_upper = true;
            } else {
                has_lower = true;
            }
        } else {
            // Any character that is not a letter or digit is considered special
            has_special = true;
        }
    }

    return has_upper && has_lower && has_digit && has_special;
}

function isLetter(char: string): boolean {
    // A character is a letter if its uppercase and lowercase versions are different
    // This properly handles unicode letters from any language
    return char.toLowerCase() !== char.toUpperCase();
}
